import { CrudList } from '../shared/CrudPage';
import { journalEntryService } from '@kss/api';
import type { JournalEntry, JournalEntryCreate, JournalEntryFilter } from '@kss/api';
import { StatusBadge } from '@kss/ui';

/**
 * Live `JournalEntry` row uses `entryNumber`, `referenceType`
 * (string FK type — "Invoice" / "Payment" / "Manual"), `narration`
 * (description), and `status` (int enum), verified 2026-05-05. The
 * legacy `reference` / `description` / `isPosted` keys never
 * resolved on real rows — every cell rendered '—'.
 */
export default function AllJournalEntries() {
  return (
    <CrudList<JournalEntry, JournalEntryCreate, JournalEntryFilter>
      title="All journal entries" newLabel="New entry" service={journalEntryService}
      idKey="journalEntryId" labelKey="entryNumber" basePath="/panel/journal-entries"
      // Explicit `module` — inferModuleFromPath would drop only the
      // trailing "s" and produce "JournalEntrie" instead of the
      // backend module "JournalEntry".
      module="JournalEntry"
      // Newest entry on top — order-like chronological grid.
      // Ref: ORDER_WORKFLOW_AUDIT #6.
      initialSortStatus={{ columnAccessor: 'entryDate', direction: 'desc' }}
      columns={[
        { accessor: 'entryNumber', title: 'Number', sortable: true, filterable: true, width: 140, render: (r) => r.entryNumber || '—' },
        { accessor: 'entryDate', title: 'Date', sortable: true, render: (r) => r.entryDate ? new Date(r.entryDate).toLocaleDateString() : '—' },
        {
          accessor: 'referenceType',
          title: 'Reference',
          sortable: true,
          filterable: true,
          render: (r) => r.referenceType || r.reference || '—',
        },
        {
          accessor: 'narration',
          title: 'Description',
          sortable: true,
          filterable: true,
          render: (r) => r.narration || r.description || '—',
        },
        { accessor: 'totalDebit', title: 'Debit (₹)', sortable: true, textAlign: 'right', render: (r) => Number(r.totalDebit ?? 0).toLocaleString('en-IN') },
        { accessor: 'totalCredit', title: 'Credit (₹)', sortable: true, textAlign: 'right', render: (r) => Number(r.totalCredit ?? 0).toLocaleString('en-IN') },
        {
          accessor: 'status',
          title: 'Status',
          sortable: true,
          render: (r) => {
            // JournalEntryStatus enum: 1=Draft, 2=Posted, 3=Reversed.
            // StatusBadge only accepts `status`, so we map the int to
            // a token whose default text matches the enum label.
            const n = typeof r.status === 'number' ? r.status : Number(r.status);
            return (
              <StatusBadge
                status={n === 2 ? 'completed' : n === 3 ? 'cancelled' : 'pending'}
              />
            );
          },
        },
      ]}
    />
  );
}
